import { Button } from "@ui/Button";
import { Card } from "@ui/Card";
import { ListTitle } from "@ui/ListTitle";
import { VStack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import { createMemo, createSignal, For, Show } from "solid-js";
import { myAskPageUrl, viewerCanAsk, viewerIsOwner } from "../askPageAccess";
import { askPageVars } from "../pageVars";
import { AskPageEmptyState } from "./AskPageEmptyState";

type Props = {
	onSelect: (question: string) => void;
};

const PAGE_SIZE = 4;

export const SuggestedQuestions = (props: Props) => {
	const vars = askPageVars();
	const pool = createMemo(() =>
		(vars.starter_questions_pool ?? []).filter(
			(question) => question.trim() !== "",
		),
	);
	const pageCount = createMemo(() =>
		pool().length === 0 ? 0 : Math.ceil(pool().length / PAGE_SIZE),
	);
	const [pageIndex, setPageIndex] = createSignal(0);

	const clampedPage = () => Math.min(pageIndex(), Math.max(0, pageCount() - 1));

	const pageQuestions = createMemo(() => {
		const start = clampedPage() * PAGE_SIZE;
		return pool().slice(start, start + PAGE_SIZE);
	});

	const goPrev = () => setPageIndex((page) => Math.max(0, page - 1));
	const goNext = () =>
		setPageIndex((page) => Math.min(pageCount() - 1, page + 1));

	const canSelectSuggestion = () =>
		viewerCanAsk(vars.is_paid_ask_page, vars.status);

	return (
		<Show
			when={pool().length > 0 && vars.has_enabled_source}
			fallback={
				<Show when={viewerIsOwner(vars.status) && vars.has_enabled_source}>
					<AskPageEmptyState
						symbol="quiz"
						title="Add starter questions"
						body="Give visitors a few one-tap questions to get them going."
						action={{
							label: "Add questions",
							href: myAskPageUrl("questions"),
						}}
					/>
				</Show>
			}
		>
			<VStack spacing="large" class="askgvt1-suggestions-section">
				<div class="askgvt1-suggestions-header">
					<div class="askgvt1-suggestions-title-row">
						<ListTitle>Suggested questions</ListTitle>
						<div class="askgvt1-suggestions-rule" aria-hidden="true" />
					</div>
				</div>
				<div class="askgvt1-suggestions">
					<For each={pageQuestions()}>
						{(question) => (
							<Show
								when={canSelectSuggestion()}
								fallback={
									<Card
										as="div"
										class="askgvt1-suggestion-card--static"
										style={{ "text-align": "left" }}
									>
										<Text size="small" weight="semi-bold" color="ink">
											{question}
										</Text>
									</Card>
								}
							>
								<Card
									as="button"
									type="button"
									onClick={() => props.onSelect(question)}
									style={{ "text-align": "left", cursor: "pointer" }}
								>
									<Text size="small" weight="semi-bold" color="ink">
										{question}
									</Text>
								</Card>
							</Show>
						)}
					</For>
				</div>
				<Show when={pageCount() > 1}>
					<div class="askgvt1-suggestions-controls">
						<Button
							type="button"
							size="small"
							variant="tertiary"
							shape="circle"
							symbol="chevron_left"
							disabled={clampedPage() === 0}
							aria-label="Previous suggested questions"
							onClick={goPrev}
						/>
						<div
							class="askgvt1-pips"
							role="tablist"
							aria-label="Suggested question pages"
						>
							<For each={Array.from({ length: pageCount() }, (_, i) => i)}>
								{(index) => (
									<Card
										as="button"
										type="button"
										noPadding
										class="askgvt1-pip"
										classList={{ "askgvt1-pip--on": clampedPage() === index }}
										aria-label={`Page ${index + 1} of ${pageCount()}`}
										aria-selected={clampedPage() === index}
										onClick={() => setPageIndex(index)}
									/>
								)}
							</For>
						</div>
						<Button
							type="button"
							size="small"
							variant="tertiary"
							symbol="chevron_right"
							shape="circle"
							disabled={clampedPage() >= pageCount() - 1}
							aria-label="Next suggested questions"
							onClick={goNext}
						/>
					</div>
				</Show>
			</VStack>
		</Show>
	);
};
